import React, { useState, useEffect } from 'react';
import { Palette, Monitor, Sun, Moon, Save, Eye } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';


const AppearanceSettings = ({ userProfile, onSave, saving }) => {
  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: 'system', // light, dark, system
    compact_mode: false,
    font_size: 'medium', // small, medium, large
    language: 'en',
    timezone: 'auto'
  });

  useEffect(() => {
    // Load saved theme from localStorage or user preferences
    const savedTheme = localStorage.getItem('theme') || 'system';
    setAppearanceSettings(prev => ({ ...prev, theme: savedTheme }));
  }, [userProfile]);

  const handleSettingChange = (setting, value) => {
    setAppearanceSettings(prev => ({ ...prev, [setting]: value }));
    
    // Apply theme immediately when changed
    if (setting === 'theme') {
      applyTheme(value);
    }
  };

  const applyTheme = (theme) => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList?.add('dark');
      localStorage.setItem('theme', 'dark');
    } else if (theme === 'light') {
      root.classList?.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      // System theme
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
      root.classList?.toggle('dark', prefersDark);
      localStorage.setItem('theme', 'system');
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({ appearance_settings: appearanceSettings });
    localStorage.setItem('appearance_settings', JSON.stringify(appearanceSettings));
  };

  const previewTheme = (theme) => {
    applyTheme(theme);
  };

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun, description: 'Clean and bright interface' },
    { value: 'dark', label: 'Dark', icon: Moon, description: 'Easy on the eyes in low light' },
    { value: 'system', label: 'System', icon: Monitor, description: 'Follows your system preference' }
  ];

  const fontSizeOptions = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'it', label: 'Italiano' },
    { value: 'pt', label: 'Português' }
  ];

  const timezoneOptions = [
    { value: 'auto', label: 'Auto-detect' },
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time' },
    { value: 'America/Chicago', label: 'Central Time' },
    { value: 'America/Denver', label: 'Mountain Time' },
    { value: 'America/Los_Angeles', label: 'Pacific Time' },
    { value: 'Europe/London', label: 'GMT' },
    { value: 'Europe/Paris', label: 'CET' },
    { value: 'Asia/Tokyo', label: 'JST' }
  ];

  const ThemeCard = ({ theme, selected, onClick }) => {
    const Icon = theme?.icon;
    return (
      <div
        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${
          selected 
            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' :'border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => onClick(theme?.value)}
      >
        <div className="flex items-center space-x-3">
          <Icon className="h-6 w-6 text-gray-600" />
          <div>
            <h3 className="font-medium text-gray-900">{theme?.label}</h3>
            <p className="text-sm text-gray-600">{theme?.description}</p>
          </div>
        </div>
        {selected && (
          <div className="absolute top-2 right-2">
            <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={(e) => {
            e?.stopPropagation();
            previewTheme(theme?.value);
          }}
          className="absolute bottom-2 right-2 text-xs text-blue-600 hover:text-blue-800 flex items-center"
        >
          <Eye className="h-3 w-3 mr-1" />
          Preview
        </button>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <Palette className="h-5 w-5 mr-2" />
          Appearance Settings
        </h2>
        <p className="text-gray-600 mt-1">
          Customize the look and feel of your ServiceWatch experience
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Theme Selection */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Theme</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {themeOptions?.map((theme) => (
              <ThemeCard
                key={theme?.value}
                theme={theme}
                selected={appearanceSettings?.theme === theme?.value}
                onClick={(value) => handleSettingChange('theme', value)}
              />
            ))}
          </div>
        </div>

        {/* Display Options */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900">Display Options</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Font Size
              </label>
              <Select
                value={appearanceSettings?.font_size}
                onValueChange={(value) => handleSettingChange('font_size', value)}
                placeholder="Select font size"
              >
                {fontSizeOptions?.map((option) => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </Select>
              <p className="text-gray-500 text-sm mt-1">
                Adjust text size for better readability
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <Select
                value={appearanceSettings?.language}
                onValueChange={(value) => handleSettingChange('language', value)}
                placeholder="Select language"
              >
                {languageOptions?.map((option) => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </Select>
              <p className="text-gray-500 text-sm mt-1">
                Choose your preferred interface language
              </p>
            </div>
          </div>
        </div>

        {/* Time & Date */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Time & Date</h3>
          
          <div className="max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timezone
            </label>
            <Select
              value={appearanceSettings?.timezone}
              onValueChange={(value) => handleSettingChange('timezone', value)}
              placeholder="Select timezone"
            >
              {timezoneOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Select>
            <p className="text-gray-500 text-sm mt-1">
              Times will be displayed in your selected timezone
            </p>
          </div>
        </div>

        {/* Accessibility */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Accessibility</h3>
          
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="compact_mode"
              checked={appearanceSettings?.compact_mode}
              onChange={(e) => handleSettingChange('compact_mode', e?.target?.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
            />
            <div>
              <label htmlFor="compact_mode" className="text-sm font-medium text-gray-900">
                Compact Mode
              </label>
              <p className="text-sm text-gray-600">
                Reduce spacing and padding for a more compact interface
              </p>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-base font-medium text-gray-900 mb-4">Preview</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 px-3 bg-white rounded border">
              <span className="text-sm text-gray-700">Sample outage notification</span>
              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Critical</span>
            </div>
            <div className="flex items-center justify-between py-2 px-3 bg-white rounded border">
              <span className="text-sm text-gray-700">Sample service status</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Operational</span>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <Button
            type="submit"
            disabled={saving}
            className="flex items-center"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Appearance Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AppearanceSettings;